import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { apiUrl } from "utils/apiUrl";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { isValidEmailAddress } from "utils/emailValidation";

const INPUT_BASE =
  "mt-2 mb-3 flex h-12 w-full items-center justify-center rounded-xl bg-white/5 p-3 text-sm text-white outline-none transition-colors placeholder:text-gray-400";
const INPUT_BORDER_OK = "border border-white/20";
const INPUT_BORDER_ERR = "border-2 border-red-500 dark:border-red-400";

const RECAPTCHA_SITE_KEY =
  typeof process.env.REACT_APP_RECAPTCHA_SITE_KEY === "string"
    ? process.env.REACT_APP_RECAPTCHA_SITE_KEY.trim()
    : "";

export default function ForgotPassword() {
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [linkSent, setLinkSent] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSendClick = async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setErrorText("Please enter your e-mail.");
      return;
    }
    if (!isValidEmailAddress(trimmed)) {
      setErrorText("Please enter a valid e-mail address.");
      return;
    }
    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      setErrorText("Please complete the reCAPTCHA.");
      return;
    }
    setErrorText("");
    setLoading(true);
    try {
      const res = await fetch(apiUrl("/api/password-reset-request"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, recaptchaToken }),
      });
      const raw = await res.text();
      let data = {};
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        try {
          data = JSON.parse(raw);
        } catch {
          data = {};
        }
      }
      recaptchaRef.current?.reset();
      if (res.ok && data.ok === true) {
        setLinkSent(true);
        return;
      }
      if (typeof data.message === "string" && data.message) {
        setErrorText(
          clientSafeMessage(
            data.message,
            "Could not send the reset link. Please try again.",
          ),
        );
        return;
      }
      setErrorText(
        clientSafeMessage(
          null,
          "Could not send the reset link. Please try again.",
        ),
      );
    } catch {
      recaptchaRef.current?.reset();
      setErrorText("Network error. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (linkSent) {
    return (
      <div className="relative z-30 mt-16 mb-16 flex min-h-[65vh] w-full flex-col items-center justify-center gap-8 px-6 text-center md:mx-0 md:px-8 lg:mb-10">
        <div className="flex max-w-2xl flex-col gap-4">
          <h2 className="text-4xl font-extrabold leading-tight text-green-400 sm:text-5xl md:text-6xl">
            Check your inbox
          </h2>
          <p className="text-base text-gray-300">
            If an account exists for that e-mail, we sent a link to reset your
            password. The link is valid for 2 hours.
          </p>
        </div>
        <Link
          to="/SignIn"
          className="linear rounded-xl bg-brand-500 px-10 py-4 text-lg font-bold text-white shadow-lg transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-brand-400 dark:hover:bg-brand-300"
        >
          Back to SignIn
        </Link>
      </div>
    );
  }

  return (
    <div className="relative z-30 mt-16 mb-16 flex h-full w-full items-center justify-center px-2 md:mx-0 md:px-0 lg:mb-10 lg:items-center lg:justify-start">
      <div className="mt-[10vh] w-full max-w-full flex-col items-center md:pl-4 lg:pl-0 xl:max-w-[420px]">
        <h4 className="mb-2.5 text-4xl font-bold text-white">
          Forgot your password?
        </h4>
        <p className="mb-9 ml-1 text-base text-gray-300">
          Enter the e-mail linked to your account. We will send you a link to
          choose a new password.
        </p>
        <label className="ml-1.5 text-sm font-medium text-gray-100">
          E-mail
          <input
            id="forgot-email"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (errorText) setErrorText("");
            }}
            placeholder="you@example.com"
            className={`${INPUT_BASE} ${errorText ? INPUT_BORDER_ERR : INPUT_BORDER_OK}`}
          />
        </label>
        {RECAPTCHA_SITE_KEY ? (
          <div className="mb-4 flex justify-center">
            <ReCAPTCHA ref={recaptchaRef} sitekey={RECAPTCHA_SITE_KEY} />
          </div>
        ) : (
          <p className="mb-4 text-sm text-amber-400">
            reCAPTCHA is not configured (REACT_APP_RECAPTCHA_SITE_KEY). Password
            reset cannot be used until it is set.
          </p>
        )}
        {errorText && (
          <p className="mb-3 text-sm text-red-400">{errorText}</p>
        )}
        <button
          type="button"
          onClick={handleSendClick}
          disabled={loading || !RECAPTCHA_SITE_KEY}
          className="linear mb-6 w-full rounded-xl bg-brand-500 py-[12px] text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 dark:bg-brand-400 dark:text-white dark:hover:bg-brand-300 dark:active:bg-brand-200"
        >
          {loading ? "Sending…" : "Send reset link"}
        </button>
      </div>
    </div>
  );
}
